import { Fragment } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 간소화된 예약 플로우 진행 표시(스텝퍼).
 * 상세 페이지 제거 후 흐름: 날짜 선택 → 차량 선택 → 예약 정보 입력 → 결제 완료.
 * 현재 스텝은 라우트로 판별하며, 플로우 밖 페이지에서는 렌더하지 않는다.
 */
const STEPS = [
  { full: '날짜 선택', short: '날짜' },
  { full: '차량 선택', short: '차량' },
  { full: '예약 정보 입력', short: '예약정보' },
  { full: '결제 완료', short: '완료' },
] as const;

/**
 * 라우트 → 현재 스텝. 결제/확인 페이지만 라우트로 판별한다.
 * 검색 페이지는 상단 고정 툴바와 겹치므로 결과 상단에서 `currentStep`을 직접 넘겨 렌더한다.
 */
const getCurrentStep = (pathname: string): number => {
  if (pathname.startsWith('/reservation/payment/confirm')) return 4;
  if (pathname.startsWith('/reservation/payment')) return 3; // payment + request
  return 0;
};

function CheckIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      className='h-3.5 w-3.5'
      fill='none'
      stroke='currentColor'
      strokeWidth={3}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M20 6 9 17l-5-5' />
    </svg>
  );
}

type Props = {
  /** 검색 페이지처럼 라우트로 판별하지 않는 경우 스텝(1~4)을 직접 지정한다. */
  currentStep?: number;
};

function ReservationStepper({ currentStep }: Props) {
  const { pathname } = useLocation();
  const current = currentStep ?? getCurrentStep(pathname);
  if (current === 0) return null;

  return (
    <nav aria-label='예약 단계' className='w-full'>
      <ol className='mx-auto flex max-w-[600px] items-center px-4 py-4 sm:py-6'>
        {STEPS.map((step, index) => {
          const step_number = index + 1;
          const isDone = step_number < current;
          const isActive = step_number === current;
          const isReached = isDone || isActive;

          return (
            <Fragment key={step.full}>
              <li className='flex shrink-0 items-center gap-2'>
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold transition-colors ${
                    isReached
                      ? 'bg-primary-050 text-grey-000'
                      : 'bg-grey-010 text-grey-050'
                  } ${isActive ? 'ring-4 ring-primary-050/15' : ''}`}
                >
                  {isDone ? <CheckIcon /> : step_number}
                </span>
                <span
                  className={`whitespace-nowrap text-[13px] ${
                    isActive
                      ? 'font-bold text-primary-050'
                      : isDone
                        ? 'font-medium text-grey-080'
                        : 'text-grey-050'
                  }`}
                >
                  <span className='hidden sm:inline'>{step.full}</span>
                  <span className='sm:hidden'>{step.short}</span>
                </span>
              </li>
              {index < STEPS.length - 1 && (
                <span
                  className={`mx-2 h-0.5 flex-1 rounded-full transition-colors ${
                    isDone ? 'bg-primary-050' : 'bg-grey-020'
                  }`}
                />
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

export default ReservationStepper;
